import { Plane } from "lucide-react";
import {
  crossesTimezone,
  flightLabel,
  formatDuration,
  formatUtcOffset,
  legDurationMinutes,
  legMoment,
  type FlightLeg,
  type FlightPassenger,
  type LocalMoment,
} from "@produce/shared";

/**
 * One flight leg, read-only.
 *
 * Presentational on purpose: it takes a leg and (optionally) the
 * personal details of the one person it is being shown to, and renders
 * them. No data fetching, so the same component serves the producer's
 * review page, the portal briefing and the flights dialog preview
 * without any of them being able to drift on formatting.
 */

function Endpoint({
  code,
  name,
  terminal,
  checkInDesk,
  moment,
  offsetMinutes,
  showOffset,
}: {
  code: string | null;
  name: string | null;
  terminal: string | null;
  checkInDesk?: string | null;
  moment: LocalMoment;
  offsetMinutes: number | null;
  showOffset: boolean;
}) {
  const offsetLabel = showOffset ? formatUtcOffset(offsetMinutes) : "";
  return (
    <div>
      <p className="text-lg font-bold text-foreground">{code || name || "—"}</p>
      <p className="text-sm text-muted-foreground">{name}</p>
      <p className="text-sm text-foreground">
        {moment.full}
        {offsetLabel && (
          <span className="ml-1 text-xs text-muted-foreground">(UTC{offsetLabel})</span>
        )}
      </p>
      {terminal && <p className="text-xs text-muted-foreground">Terminal {terminal}</p>}
      {checkInDesk && <p className="text-xs text-muted-foreground">Check-in {checkInDesk}</p>}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div>
      <span className="text-xs text-muted-foreground">{label}</span>
      <p className="text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}

export function FlightLegRow({
  leg,
  passenger,
}: {
  leg: FlightLeg;
  passenger?: FlightPassenger | null;
}) {
  const duration = formatDuration(legDurationMinutes(leg));
  // Only annotate the UTC offset when the two ends differ — on a
  // domestic hop it is noise.
  const showOffset = crossesTimezone(leg);

  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="mb-3 flex items-center gap-2">
        <Plane className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span className="text-sm font-semibold text-foreground">
          {flightLabel(leg) || "Flight"}
        </span>
        {duration && <span className="text-xs text-muted-foreground">· {duration}</span>}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Endpoint
          code={leg.departure_airport}
          name={leg.departure_airport_name}
          terminal={leg.departure_terminal}
          checkInDesk={leg.departure_check_in_desk}
          moment={legMoment(leg, "departure")}
          offsetMinutes={leg.departure_utc_offset_minutes}
          showOffset={showOffset}
        />
        <Endpoint
          code={leg.arrival_airport}
          name={leg.arrival_airport_name}
          terminal={leg.arrival_terminal}
          moment={legMoment(leg, "arrival")}
          offsetMinutes={leg.arrival_utc_offset_minutes}
          showOffset={showOffset}
        />
      </div>

      {(passenger?.booking_reference ||
        passenger?.seat ||
        passenger?.baggage ||
        passenger?.special_baggage ||
        leg.aircraft) && (
        <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-3 sm:grid-cols-4">
          <Detail label="Booking reference" value={passenger?.booking_reference ?? null} />
          <Detail label="Seat" value={passenger?.seat ?? null} />
          <Detail label="Baggage" value={passenger?.baggage ?? null} />
          <Detail label="Equipment" value={passenger?.special_baggage ?? null} />
          <Detail label="Aircraft" value={leg.aircraft} />
        </div>
      )}

      {leg.notes && (
        <p className="mt-3 whitespace-pre-wrap border-t border-border pt-3 text-sm text-muted-foreground">
          {leg.notes}
        </p>
      )}
    </div>
  );
}
